import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import type { Requirement, SectionReadiness } from "../api/types";
import { ErrorState, Notice, PageHeader, SkeletonBlock, StatusBadge } from "../components/ui";
import { useDossier } from "../dossier";
import { label, w } from "../i18n";
import { readinessLabel, readinessStatus, requirementStatus } from "../status";

const SCOPE = ["A", "B", "C", "D", "E"];

export function RequirementRow({ requirement }: { requirement: Requirement }) {
  const { openUpload, inspect } = useDossier();
  return <div className="ws-req">
    <StatusBadge status={requirementStatus(requirement.state)} label={w(`ready.state.${requirement.state}`)} />
    <span className="ws-req-label">{label("req", requirement.key, requirement.label)}
      <span className="ws-faint ws-small"> · {w(requirement.required ? "ready.required" : "ready.optional")}</span>
      {requirement.via === "extracted" && <span className="ws-faint ws-small"> · {w("ready.viaExtracted")}</span>}
      {requirement.documents.length > 0 && <span className="ws-req-docs" style={{ display: "block" }}>
        {requirement.documents.map((document, index) => <span key={document.id}>{index > 0 && ", "}
          <button className="ws-link" onClick={() => inspect(document.id)}>{document.name}</button></span>)}</span>}
    </span>
    {requirement.state === "missing" && <button className="ws-btn sm" onClick={() => openUpload()}><Plus size={12} />{w("ready.addDocument")}</button>}
    {requirement.state === "pending" && requirement.documents[0] &&
      <button className="ws-btn sm" onClick={() => inspect(requirement.documents[0].id)}>{w("ready.reviewDocument")}</button>}
  </div>;
}

export function SectionReadinessCard({ section, base }: { section: SectionReadiness; base: string }) {
  return <div className="ws-ready-section">
    <div className="ws-ready-head">
      <h3><Link className="ws-link" to={`${base}/sections/${section.code}`}><span className="ws-mono">{section.code}</span>&nbsp; {section.title}</Link></h3>
      <StatusBadge status={readinessStatus(section.status)} label={readinessLabel(section.status)} />
    </div>
    {section.requirements.map(requirement => <RequirementRow key={requirement.key} requirement={requirement} />)}
  </div>;
}

export function ReadinessPage() {
  const { dossier, readiness } = useDossier();
  const base = `/dossiers/${dossier.id}`;
  const data = readiness.data;
  return <>
    <PageHeader title={w("ready.title")} lead={w("ready.lead")} />
    {readiness.loading && <SkeletonBlock lines={10} />}
    {readiness.error && <ErrorState title={w("ready.loadError")} error={readiness.error} onRetry={readiness.reload} />}
    {data && <div className="ws-stack">
      {data.documents_in_progress.length > 0 && <Notice tone="info">{w("ready.inProgress", { count: data.documents_in_progress.length })}</Notice>}
      {data.chapters.filter(chapter => SCOPE.includes(chapter.code)).map(chapter => <section key={chapter.code} className="ws-card">
        <div className="ws-card-head"><h2>{w("nav.chapterTitle", { code: chapter.code, title: chapter.title })}</h2></div>
        {chapter.sections.map(section => <SectionReadinessCard key={section.code} section={section} base={base} />)}
      </section>)}
    </div>}
  </>;
}
